import React, { useEffect, useMemo, useState } from "react";

import { Box } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";

import Button, { Color } from "../../../../../../components/Form/Button";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";

import SkeletonComponent from "../../../../../../components/Generic/Skeleton";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../context/StateContext";
import GroupMfaSettingsService, {
  GroupMfaSettings,
  MfaMethod,
  MfaEnvironment,
  UpdateGroupMfaSettingsDto,
} from "../../../../../../services/group-mfa-settings.service";
import GroupService from "../../../../../../services/group.service";
import { Group } from "../../../../../../models/group";
import MfaGroupCard from "./components/MfaGroupCard";
import { GeneralWrapper, OptionsWrapper, Title } from "../styles";
import useStyles from "../styles";
import PaginationComponent from "Components/PaginationComponent/PaginationComponent";

interface MFAComponentProps {
  onBack?: () => void;
  inline?: boolean;
}

const MFAComponent: React.FC<MFAComponentProps> = ({ onBack, inline }) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const [groups, setGroups] = useState<Group[]>([]);
  const [mfaSettings, setMfaSettings] = useState<GroupMfaSettings[]>([]);
  const [loadingGroupId, setLoadingGroupId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const { data: groupsData, isLoading: isLoadingGroups } = useQuery(
    ["groups.list", currentUser?.companyId],
    () =>
      GroupService.getAll({
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response?.data && Array.isArray(response.data)) {
          setGroups(response.data);
        }
      },
    }
  );

  const {
    data: mfaSettingsData,
    isLoading: isLoadingSettings,
    refetch: refetchMfaSettings,
  } = useQuery(
    ["group-mfa-settings.list", currentUser?.companyId],
    () => GroupMfaSettingsService.getAllGroupMfaSettings(currentUser?.companyId || 0),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response?.data && Array.isArray(response.data)) {
          setMfaSettings(response.data);
        }
      },
    }
  );

  const { mutate: updateMfaSettings } = useMutation(
    (data: { groupId: number; settings: Omit<UpdateGroupMfaSettingsDto, "groupId"> }) =>
      GroupMfaSettingsService.updateGroupMfaSettings(data.groupId, data.settings),
    {
      onSuccess: (response, variables) => {
        setLoadingGroupId(null);

        if (response?.data) {
          setMfaSettings((prev) => {
            const index = prev.findIndex((s) => s.groupId === variables.groupId);
            if (index >= 0) {
              const newSettings = [...prev];
              newSettings[index] = response.data;
              return newSettings;
            }
            return [...prev, response.data];
          });

          NotificationToast.success(translate("mfaSettingsUpdated"), { duration: 3000 });
        }

        refetchMfaSettings();
      },
      onError: (error: any) => {
        setLoadingGroupId(null);
        const message = error?.response?.data?.message || translate("errorUpdatingMfaSettings");
        NotificationToast.error(message, { duration: 5000 });
      },
    }
  );

  const handleUpdateMfaSettings = (
    groupId: number,
    enabled: boolean,
    method?: MfaMethod,
    environments?: MfaEnvironment[]
  ) => {
    setLoadingGroupId(groupId);
    updateMfaSettings({
      groupId,
      settings: {
        enabled,
        method,
        environments,
      },
    });
  };

  const getGroupMfaSettings = (groupId: number): GroupMfaSettings | undefined => {
    return mfaSettings.find((s) => s.groupId === groupId);
  };

  const filteredGroups = useMemo(() => {
    return groups.filter((g): g is Group & { id: number } => g.id != null);
  }, [groups]);

  const total = filteredGroups.length;

  const totalPages = useMemo(() => {
    if (!pageSize) return 1;
    return Math.max(1, Math.ceil(total / pageSize));
  }, [total, pageSize]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(1);
    }
  }, [page, totalPages]);

  const paginatedGroups = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredGroups.slice(startIndex, endIndex);
  }, [filteredGroups, page, pageSize]);

  const isLoading = isLoadingGroups || isLoadingSettings;

  const content = (
    <Box
      className={classes.contentSection}
      sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: "100%", minWidth: 0 }}
    >
      {!inline && onBack && (
        <Box>
          <Button
            type="button"
            startIcon={<ArrowBack />}
            onClick={onBack}
            customColor={Color.GRAY_600_TEXT}
          >
            {translate("back")}
          </Button>
        </Box>
      )}
      {!inline && (
        <Box>
          <Title>{translate("mfaMultiFactorAuthentication")}</Title>
        </Box>
      )}
      <Box className={classes.wrapperContentPlatforms} sx={{ maxWidth: "100%", minWidth: 0 }}>
        {isLoading ? (
          <SkeletonComponent isLoading={true}>
            <div style={{ height: "200px" }} />
          </SkeletonComponent>
        ) : (
          <>
            {paginatedGroups.map((group) => {
              const settings = getGroupMfaSettings(group.id);
              const isUpdating = loadingGroupId === group.id;

              return (
                <MfaGroupCard
                  key={group.id}
                  group={group}
                  settings={settings}
                  onUpdate={handleUpdateMfaSettings}
                  isLoading={isUpdating}
                />
              );
            })}
          </>
        )}

        <Box>
          <PaginationComponent
            total={isLoading ? 0 : total}
            currentPage={page}
            changePage={(page: number) => setPage(page)}
            pageSize={pageSize}
            changePageSize={(pageSize: number) => {
              setPageSize(pageSize);
              setPage(1);
            }}
            pageSizeOptions={[5, 10, 20, 25]}
          />
        </Box>
      </Box>
    </Box>
  );

  if (inline) {
    return (
      <Box sx={{ pt: 1, pb: 2, maxWidth: "100%", minWidth: 0, overflow: "hidden" }}>{content}</Box>
    );
  }

  return <GeneralWrapper>{content}</GeneralWrapper>;
};

export default MFAComponent;
